/**
 * Early-capture store for the browser's `beforeinstallprompt` event.
 *
 * Chrome/Edge fire `beforeinstallprompt` ONCE, very early — frequently before
 * React has mounted. If the listener lives inside a component effect it misses
 * the event, so clicking "Install app" can't open the native install
 * confirmation and falls back to manual instructions. Attaching the listener at
 * module load (this file is imported first in `main.tsx`) captures the event
 * the moment it fires and parks it here for the UI to use later.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop Chrome's mini-infobar so we control when the native prompt shows.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

/** The captured install event, or null if the browser hasn't offered one. */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/** Clear the stored event after a successful install / accepted prompt. */
export function clearInstallPrompt(): void {
  deferredPrompt = null;
  emit();
}

/** Subscribe to capture/clear changes; returns an unsubscribe fn. */
export function subscribeInstallPrompt(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
